/// <reference types="vite/client" />
/**
 * <LeadDrawer /> — right-side slide-in panel showing a single lead's
 * detail, audit trail, opportunity notes, and manual CRM transitions.
 * Phase 9 Y6 (#166).
 *
 * Open the drawer by setting the `leadId` prop; close it with the X
 * button (calls `onClose`). The parent passes `onTransitioned` to be
 * notified when a status transition or note save succeeds, so the
 * page can refetch the list.
 */

import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { useAgentSocketContext } from "../hooks/useAgentSocketContext";

interface Lead {
  id: string;
  engagementId: string;
  source: string;
  companyName: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  status: string;
  bantScore: number | null;
  bantReasoning: string | null;
  warmScore?: number;
  createdAt: string | number | null;
  updatedAt: string | number | null;
}

interface AutoReplyDraftRow {
  id: string;
  channel: string;
  draft_text: string;
  source_text: string | null;
  source_message_id: string | null;
  status: string;
  created_at: number;
}

interface Opportunity {
  id: string;
  stage: string;
  estimatedValueCents: number | null;
  notesMd: string | null;
}

interface AuditEntry {
  id: string;
  createdAt: string;
  action: string;
  details: Record<string, unknown> | null;
}

interface EmailEvent {
  id: string;
  createdAt: string;
  eventType: string;
  url: string | null;
  provenance: string;
  clientLabel: string | null;
  country: string | null;
  city: string | null;
}

interface LeadDetail {
  lead: Lead;
  opportunity: Opportunity | null;
  auditTrail: AuditEntry[];
  emailEvents?: EmailEvent[];
}

interface LeadDrawerProps {
  leadId: string | null;
  onClose: () => void;
  /** Called after a successful status transition or note save. */
  onTransitioned?: () => void;
}

const TRANSITION_BUTTONS: Array<{ to: string; label: string }> = [
  { to: "contacted", label: "Mark contacted" },
  { to: "qualified", label: "Mark qualified" },
  { to: "won", label: "Mark won" },
  { to: "lost", label: "Mark lost" },
];

const SK_TOKEN = "levier.admin.token";

function authHeaders(): Record<string, string> {
  const token = typeof window !== "undefined" ? window.sessionStorage.getItem(SK_TOKEN) ?? "" : "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function LeadDrawer({ leadId, onClose, onTransitioned }: LeadDrawerProps) {
  const [detail, setDetail] = useState<LeadDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [busyTo, setBusyTo] = useState<string | null>(null);

  const open = leadId !== null;

  // Fetch on open.
  useEffect(() => {
    if (!leadId) {
      setDetail(null);
      setError(null);
      setNote("");
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetch(`/api/leads/${encodeURIComponent(leadId)}`, { signal: ctrl.signal, headers: authHeaders() })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status}${body ? ` — ${body.slice(0, 200)}` : ""}`);
        }
        return res.json() as Promise<LeadDetail>;
      })
      .then((d) => {
        setDetail(d);
        setNote(d.opportunity?.notesMd ?? "");
        setLoading(false);
      })
      .catch((err: Error) => {
        if (err.name === "AbortError") return;
        setError(err.message);
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [leadId]);

  // Close on Escape + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const handleTransition = useCallback(
    async (to: string) => {
      if (!leadId) return;
      setBusyTo(to);
      try {
        const res = await fetch("/api/crm/transition", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entity_type: "lead", entity_id: leadId, to_status: to }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { message?: string };
          throw new Error(body.message ?? `HTTP ${res.status}`);
        }
        // Refetch detail.
        const refresh = await fetch(`/api/leads/${encodeURIComponent(leadId)}`, { headers: authHeaders() });
        if (refresh.ok) setDetail((await refresh.json()) as LeadDetail);
        onTransitioned?.();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyTo(null);
      }
    },
    [leadId, onTransitioned],
  );

  const handleSaveNote = useCallback(async () => {
    if (!leadId) return;
    setSavingNote(true);
    try {
      const res = await fetch(`/api/leads/${encodeURIComponent(leadId)}/note`, {
          headers: { "Content-Type": "application/json", ...authHeaders() },
          method: "POST",
          body: JSON.stringify({ note }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `HTTP ${res.status}`);
      }
      onTransitioned?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingNote(false);
    }
  }, [leadId, note, onTransitioned]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop — hidden under sm where the drawer is fullscreen. */}
      <div
        onClick={onClose}
        className="hidden sm:block fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
        aria-hidden
      />

      {/* Drawer — fullscreen on mobile, side panel on sm+. */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Lead detail"
        className="fixed inset-0 sm:inset-y-0 sm:right-0 sm:left-auto z-50 sm:h-full sm:w-[440px] sm:max-w-[90vw] overflow-y-auto sm:border-l border-[var(--color-border)] flex flex-col"
        style={{ background: "var(--color-bg-elev)" }}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-5 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-elev)]/95 backdrop-blur-sm">
          <p className="eyebrow">Lead Detail</p>
          <button
            onClick={onClose}
            aria-label="Close drawer"
            className="size-11 -mr-2 grid place-items-center rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/5 transition-colors"
          >
            <X className="size-5" strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          {loading && (
            <p className="font-mono text-xs tracking-[0.18em] uppercase text-[var(--color-text-dim)] blink">
              Loading lead…
            </p>
          )}

          {error && (
            <div
              className="panel p-3"
              style={{ borderColor: "var(--color-status-error)", background: "rgba(239,68,68,0.04)" }}
            >
              <p className="eyebrow" style={{ color: "var(--color-status-error)" }}>Error</p>
              <p className="font-mono text-xs mt-1 text-[var(--color-text-muted)]">{error}</p>
            </div>
          )}

          {detail && (
            <>
              <header>
                <h3 className="font-display italic text-3xl leading-tight text-[var(--color-text)]">
                  {detail.lead.companyName}
                </h3>
                <p className="mt-1 font-mono text-[10px] tracking-wider uppercase text-[var(--color-text-dim)]">
                  {detail.lead.source} · {detail.lead.status}
                </p>
              </header>

              <section className="space-y-1 text-sm">
                {detail.lead.contactName && (
                  <p className="text-[var(--color-text)]">{detail.lead.contactName}</p>
                )}
                {detail.lead.contactEmail && (
                  <p className="font-mono text-xs text-[var(--color-text-muted)]">{detail.lead.contactEmail}</p>
                )}
                {detail.lead.contactPhone && (
                  <p className="font-mono text-xs text-[var(--color-text-muted)]">{detail.lead.contactPhone}</p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  {detail.lead.bantScore != null && (
                    <span
                      className="pill"
                      style={{
                        color: "var(--color-text)",
                        borderColor: "var(--color-border)",
                        background: "var(--color-bg-card)",
                      }}
                    >
                      BANT {detail.lead.bantScore}
                    </span>
                  )}
                  {(detail.lead.warmScore ?? 0) > 0 && (
                    <span
                      className="pill"
                      title={`Profile views +5, Invite accepts +20, Messages received +30 — niveau actuel ${
                        (detail.lead.warmScore ?? 0) > 100 ? "très chaud"
                          : (detail.lead.warmScore ?? 0) > 30 ? "chaud"
                          : (detail.lead.warmScore ?? 0) > 10 ? "tiède"
                          : "froid"
                      }`}
                      style={{
                        color: (detail.lead.warmScore ?? 0) > 100
                          ? "var(--color-status-error)"
                          : (detail.lead.warmScore ?? 0) > 30
                            ? "var(--color-accent-bright)"
                            : (detail.lead.warmScore ?? 0) > 10
                              ? "var(--color-accent)"
                              : "var(--color-text-muted)",
                        borderColor: "var(--color-border)",
                        background: "var(--color-bg-card)",
                      }}
                    >
                      🌡 {detail.lead.warmScore}
                    </span>
                  )}
                </div>
                {detail.lead.bantReasoning && (
                  <p className="font-mono text-[10px] text-[var(--color-text-dim)] mt-1">
                    {detail.lead.bantReasoning}
                  </p>
                )}
              </section>

              {/* Audit envoyé */}
              {detail.lead.engagementId && (
                <section className="space-y-2">
                  <p className="eyebrow">Audit envoyé</p>
                  <a
                    href={`/audits?engagement=${encodeURIComponent(detail.lead.engagementId)}`}
                    className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded transition-colors"
                    style={{
                      background: "var(--color-bg-card)",
                      color: "var(--color-text)",
                      border: "1px solid var(--color-border)",
                    }}
                  >
                    Voir le rapport →
                  </a>
                </section>
              )}

              {/* Auto-reply drafts pending (U1) */}
              {leadId && <AutoReplyDraftsSection leadId={leadId} />}

              {/* Email tracking */}
              <EmailTrackingSection events={detail.emailEvents ?? []} />

              {/* Transitions */}
              <section className="space-y-2">
                <p className="eyebrow">Transitions</p>
                <div className="grid grid-cols-2 gap-2">
                  {TRANSITION_BUTTONS.map((b) => {
                    const current = detail.lead.status === b.to;
                    return (
                      <button
                        key={b.to}
                        onClick={() => handleTransition(b.to)}
                        disabled={current || busyTo === b.to}
                        className="font-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{
                          background: current ? "var(--color-accent-soft)" : "var(--color-bg-card)",
                          color: current ? "var(--color-accent-glow)" : "var(--color-text)",
                          border: `1px solid ${current ? "var(--color-accent)" : "var(--color-border)"}`,
                        }}
                      >
                        {busyTo === b.to ? "…" : b.label}
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Note */}
              <section className="space-y-2">
                <p className="eyebrow">Note (markdown)</p>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={6}
                  className="w-full font-mono text-xs px-3 py-2 rounded resize-y"
                  style={{
                    background: "var(--color-bg-card)",
                    color: "var(--color-text)",
                    border: "1px solid var(--color-border)",
                  }}
                  placeholder="Discovery notes, next action, blockers…"
                />
                <button
                  onClick={handleSaveNote}
                  disabled={savingNote}
                  className="font-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded disabled:opacity-40"
                  style={{
                    background: "var(--color-accent-soft)",
                    color: "var(--color-accent-glow)",
                    border: "1px solid var(--color-accent)",
                  }}
                >
                  {savingNote ? "Saving…" : "Save note"}
                </button>
              </section>

              {/* Opportunity */}
              {detail.opportunity && (
                <section className="space-y-1">
                  <p className="eyebrow">Opportunity</p>
                  <p className="font-mono text-[11px] text-[var(--color-text-muted)]">
                    stage: <span className="text-[var(--color-text)]">{detail.opportunity.stage}</span>
                    {detail.opportunity.estimatedValueCents != null && (
                      <>
                        {" · "}
                        €{(detail.opportunity.estimatedValueCents / 100).toLocaleString("fr-FR")}
                      </>
                    )}
                  </p>
                </section>
              )}

              {/* Audit trail */}
              <section className="space-y-2">
                <p className="eyebrow">Audit trail</p>
                {detail.auditTrail.length === 0 ? (
                  <p className="font-mono text-[10px] text-[var(--color-text-dim)]">No transitions yet.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {detail.auditTrail.map((a) => {
                      const from = (a.details as { from?: string } | null)?.from ?? "?";
                      const to = (a.details as { to?: string } | null)?.to ?? "?";
                      return (
                        <li key={a.id} className="font-mono text-[10px] text-[var(--color-text-muted)]">
                          <span className="text-[var(--color-text-dim)]">{new Date(a.createdAt).toLocaleString("en-GB", { hour12: false })}</span>
                          {" · "}
                          {from} → <span className="text-[var(--color-text)]">{to}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

/** Provenance values we treat as "real human signal" directly.
 *  Anything else is noise unless rescued by the gmail-proxy heuristic
 *  below (gmail_proxy event #2+ on the same lead = real human view
 *  through Gmail's image proxy, since Gmail only pre-fetches once at
 *  delivery and re-fetches on subsequent views because of our
 *  Cache-Control: no-store header). */
const CONFIRMED_PROVENANCES = new Set(["confirmed", "unknown"]);

function isConfirmed(e: EmailEvent): boolean {
  return CONFIRMED_PROVENANCES.has(e.provenance);
}

/**
 * Walk events chronologically and tag every `gmail_proxy` event past
 * the first as a "human view through proxy". The first stays as a
 * prefetch (delivery noise).
 */
function gmailProxyHumanIds(events: EmailEvent[]): Set<string> {
  const ids = new Set<string>();
  let seenFirst = false;
  // events come pre-sorted DESC; reverse for chronological order.
  for (const e of [...events].reverse()) {
    if (e.provenance !== "gmail_proxy") continue;
    if (!seenFirst) {
      seenFirst = true;
      continue;
    }
    ids.add(e.id);
  }
  return ids;
}

function isHumanSignal(e: EmailEvent, gmailHumans: Set<string>): boolean {
  return isConfirmed(e) || gmailHumans.has(e.id);
}

/**
 * The chronologically earliest event proves the email was delivered —
 * a proxy/MPP/scanner can only fire on a message that actually
 * reached an inbox. We surface it as a "Reçu" signal independent of
 * the open/click counts.
 */
function findDeliveryEvent(events: EmailEvent[]): EmailEvent | null {
  if (events.length === 0) return null;
  // events come DESC from the API; oldest is the last in the array.
  return events[events.length - 1];
}

const PROVENANCE_LABEL: Record<string, string> = {
  gmail_proxy: "Gmail proxy",
  yahoo_proxy: "Yahoo proxy",
  aol_proxy: "AOL proxy",
  protonmail_proxy: "ProtonMail proxy",
  apple_mpp_suspected: "Apple MPP",
  outlook_safelinks: "Outlook SafeLinks",
  mimecast: "Mimecast",
  proofpoint: "Proofpoint",
  barracuda: "Barracuda",
  symantec: "Symantec",
  cisco_ironport: "Cisco IronPort",
  sophos: "Sophos",
  trendmicro: "Trend Micro",
  zscaler: "Zscaler",
  mcafee: "McAfee",
  corporate_scanner: "Scanner entreprise",
  bot: "Bot",
};

function provenanceLabel(p: string): string {
  return PROVENANCE_LABEL[p] ?? p;
}

function summarizeNoise(events: EmailEvent[]): string | null {
  if (events.length === 0) return null;
  const counts = new Map<string, number>();
  for (const e of events) counts.set(e.provenance, (counts.get(e.provenance) ?? 0) + 1);
  return Array.from(counts.entries())
    .map(([p, n]) => `${n} ${provenanceLabel(p)}`)
    .join(" · ");
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function clientDescriptor(e: EmailEvent): string {
  const parts: string[] = [];
  if (e.clientLabel) parts.push(e.clientLabel);
  const geo = [e.city, e.country].filter(Boolean).join(", ");
  if (geo) parts.push(geo);
  return parts.join(" · ");
}

function AutoReplyDraftsSection({ leadId }: { leadId: string }) {
  const { subscribe } = useAgentSocketContext();
  const [drafts, setDrafts] = useState<AutoReplyDraftRow[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/leads/${encodeURIComponent(leadId)}/auto-reply-drafts`, { headers: authHeaders() });
      if (!res.ok) return;
      const { drafts: d } = (await res.json()) as { drafts: AutoReplyDraftRow[] };
      setDrafts(d);
    } catch {
      // silent — keep last good state
    }
  }, [leadId]);

  // Phase 13 U2 #294 — bootstrap once, refresh on support.* events
  // for this specific lead. No more polling.
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const refreshIfThisLead = (e: { payload: { leadId: string } }) => {
      if (e.payload.leadId === leadId) void load();
    };
    const unsubs = [
      subscribe("support.auto_reply_drafted", refreshIfThisLead),
      subscribe("support.auto_reply_sent", refreshIfThisLead),
    ];
    return () => { unsubs.forEach((u) => u()); };
  }, [leadId, subscribe, load]);

  const patch = useCallback(async (id: string, body: { draft_text?: string; status?: string }) => {
    setBusyId(id);
    try {
      const res = await fetch(`/api/auto-reply-drafts/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok && res.status !== 409) {
        console.warn(`[auto-reply-drafts] PATCH ${id} failed: HTTP ${res.status}`);
      }
      await load();
    } finally {
      setBusyId(null);
    }
  }, [load]);

  if (drafts.length === 0) return null;

  return (
    <section className="space-y-2">
      <p className="eyebrow">Réponse auto suggérée ({drafts.length})</p>
      {drafts.map((d) => {
        const draftText = edits[d.id] ?? d.draft_text;
        const dirty = edits[d.id] !== undefined && edits[d.id] !== d.draft_text;
        return (
          <div
            key={d.id}
            className="panel p-3 space-y-2"
            style={{ background: "var(--color-bg-card)" }}
          >
            {d.source_text && (
              <blockquote
                className="font-mono text-[11px] text-[var(--color-text-muted)] pl-3 italic"
                style={{ borderLeft: "2px solid var(--color-border)" }}
              >
                {d.source_text.slice(0, 250)}
                {d.source_text.length > 250 ? "…" : ""}
              </blockquote>
            )}
            <textarea
              value={draftText}
              onChange={(e) => setEdits({ ...edits, [d.id]: e.target.value })}
              rows={5}
              className="w-full p-2 rounded font-sans text-sm resize-y"
              style={{ background: "var(--color-bg)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
            />
            <div className="flex items-center gap-2 text-[10px]">
              <span className="font-mono text-[var(--color-text-dim)] uppercase tracking-wider">
                {d.channel} · {draftText.length} chars
              </span>
              <span className="ml-auto" />
              {dirty && (
                <button
                  disabled={busyId === d.id}
                  onClick={() => patch(d.id, { draft_text: draftText })}
                  className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded"
                  style={{ color: "var(--color-accent-glow)", background: "var(--color-accent-soft)", border: "1px solid var(--color-accent)" }}
                >
                  Save
                </button>
              )}
              <button
                disabled={busyId === d.id}
                onClick={() => patch(d.id, { draft_text: draftText, status: "approved" })}
                className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded"
                style={{ color: "var(--color-status-active)", background: "rgba(132,204,22,0.06)", border: "1px solid rgba(132,204,22,0.45)" }}
              >
                Approve & Send
              </button>
              <button
                disabled={busyId === d.id}
                onClick={() => patch(d.id, { status: "rejected" })}
                className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded"
                style={{ color: "var(--color-status-error)", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.4)" }}
              >
                Reject
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
}

function EmailTrackingSection({ events }: { events: EmailEvent[] }) {
  if (events.length === 0) return null;

  const opens = events.filter((e) => e.eventType === "open");
  const clicks = events.filter((e) => e.eventType === "click");
  // Promote 2nd+ Gmail-proxy opens to "human signal" (see helper).
  const gmailHumans = gmailProxyHumanIds(opens);
  // The earliest open is the proof-of-delivery — surface it separately
  // and exclude it from both "human" and "noise" buckets so we never
  // double-count it in the UI.
  const deliveryEvent = findDeliveryEvent(opens);
  const confirmedOpens = opens.filter(
    (e) => isHumanSignal(e, gmailHumans) && e.id !== deliveryEvent?.id,
  );
  const noisyOpens = opens.filter(
    (e) => !isHumanSignal(e, gmailHumans) && e.id !== deliveryEvent?.id,
  );
  // Clicks: no proxy-rescue heuristic — gmail-proxy on click is rare
  // and would mean the user's mail client fetched the link, not a
  // human click.
  const confirmedClicks = clicks.filter(isConfirmed);
  const noisyClicks = clicks.filter((e) => !isConfirmed(e));

  // events come pre-sorted DESC; reverse confirmed opens to render the
  // timeline chronologically (oldest → newest) for natural reading.
  const openTimeline = [...confirmedOpens].reverse();

  return (
    <section className="space-y-3">
      <p className="eyebrow">Tracking email</p>

      {deliveryEvent && (
        <div className="flex items-baseline gap-2 text-sm">
          <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] w-24">
            Reçu
          </span>
          <span className="text-[var(--color-text)]">
            <span style={{ color: "var(--color-status-active)" }}>●</span>{" "}
            {fmtTime(deliveryEvent.createdAt)}
            <span className="text-[var(--color-text-muted)] font-mono text-[11px]">
              {deliveryEvent.provenance !== "confirmed" && (
                <> · via {provenanceLabel(deliveryEvent.provenance)}</>
              )}
              {clientDescriptor(deliveryEvent) && (
                <> · {clientDescriptor(deliveryEvent)}</>
              )}
            </span>
          </span>
        </div>
      )}

      <div className="space-y-1.5 text-sm">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] w-24">
            Ouvert
          </span>
          {confirmedOpens.length === 0 ? (
            <span className="font-mono text-[10px] text-[var(--color-text-dim)]">—</span>
          ) : (
            <span className="text-[var(--color-text)]">
              <span style={{ color: "var(--color-status-active)" }}>●</span>{" "}
              {confirmedOpens.length}x
            </span>
          )}
        </div>
        {openTimeline.length > 0 && (
          <div className="pl-26 ml-26 flex flex-wrap items-center gap-x-1 gap-y-1 text-[11px] font-mono text-[var(--color-text-muted)]" style={{ paddingLeft: "6.5rem" }}>
            {openTimeline.map((e, i) => (
              <span key={e.id} className="flex items-center gap-1">
                {i > 0 && <span className="text-[var(--color-text-dim)]">→</span>}
                <span title={clientDescriptor(e) || undefined}>{fmtTime(e.createdAt)}</span>
              </span>
            ))}
          </div>
        )}
        {confirmedOpens[0] && (
          <div className="text-[11px] font-mono text-[var(--color-text-dim)]" style={{ paddingLeft: "6.5rem" }}>
            {clientDescriptor(confirmedOpens[0])}
          </div>
        )}
        {noisyOpens.length > 0 && (
          <div className="text-[10px] font-mono text-[var(--color-text-dim)]" style={{ paddingLeft: "6.5rem" }}>
            + {summarizeNoise(noisyOpens)}
          </div>
        )}
      </div>

      <div className="space-y-1.5 text-sm">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] w-24">
            Cliqué
          </span>
          {confirmedClicks.length === 0 ? (
            <span className="font-mono text-[10px] text-[var(--color-text-dim)]">—</span>
          ) : (
            <span className="text-[var(--color-text)]">
              <span style={{ color: "var(--color-accent-bright)" }}>●</span>{" "}
              {confirmedClicks.length}x
            </span>
          )}
        </div>
        {confirmedClicks.length > 0 && (
          <ul className="space-y-0.5" style={{ paddingLeft: "6.5rem" }}>
            {confirmedClicks.slice(0, 3).map((c) => (
              <li key={c.id} className="text-[11px] font-mono text-[var(--color-text-muted)]">
                {fmtTime(c.createdAt)}
                {c.url && (
                  <>
                    {" · "}
                    <span className="break-all">
                      {c.url.length > 50 ? c.url.slice(0, 47) + "…" : c.url}
                    </span>
                  </>
                )}
              </li>
            ))}
            {confirmedClicks.length > 3 && (
              <li className="text-[10px] font-mono text-[var(--color-text-dim)]">
                + {confirmedClicks.length - 3} autres
              </li>
            )}
          </ul>
        )}
        {noisyClicks.length > 0 && (
          <div className="text-[10px] font-mono text-[var(--color-text-dim)]" style={{ paddingLeft: "6.5rem" }}>
            + {summarizeNoise(noisyClicks)}
          </div>
        )}
      </div>
    </section>
  );
}
